import React, { useEffect, useState } from 'react';
import { Button } from 'components/ui/button';
import { ProductModel } from 'models/ProductModel';
import useProductsService from 'api/products/ProductService';
import { useCart } from 'services/CartService';
import { Minus, Plus } from 'lucide-react';

interface ProductDetailProps {
  idProduct: string;
  open: boolean;
  onClose: React.Dispatch<React.SetStateAction<boolean>>;
}

const ProductDetailModal: React.FC<ProductDetailProps> = ({ idProduct, open, onClose }) => {
  const [product, setProduct] = useState<ProductModel | null>(null);
  const [quantity, setQuantity] = useState(0);
  const [loading, setLoading] = useState(false);
  const { addToCart, removeFromCart, getCart } = useCart();
  const { getProductById } = useProductsService();

  useEffect(() => {
    const getProduct = async () => {
      if (idProduct) {
        setLoading(true);
        const cart = getCart();
        const theProduct = await getProductById({ productId: idProduct });
        console.log(theProduct)
        const productFromCart = cart.find(item => item.productId === theProduct.productId);
        setQuantity(productFromCart ? productFromCart.quantity : 0);
        setProduct(theProduct);
        setLoading(false);
      }
    };

    if (open) {
      getProduct();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idProduct]);

  const handleAddToCart = () => {
    if (product?.productId) {
      addToCart(product.productId, 1, true);
      setQuantity(prev => prev + 1);
    }
  };

  const handleRemoveFromCart = () => {
    if (product?.productId && quantity > 0) {
      removeFromCart(product.productId, 1);
      setQuantity(prev => prev - 1);
    }
  };

  const closeModal = () => {
    setLoading(false);
    onClose(false);
  }

  function formatUnit(minimum?:number, unitFormat?:string) {
    // Vérifie si on doit mettre au pluriel (pas pour les abréviations ≤ 3 caractères)
    if (minimum && unitFormat && minimum > 1 && unitFormat.length > 3) {
      // Si l'unité finit par "eau" (ex: "morceau"), on ajoute "x", sinon "s"
      if (unitFormat.endsWith("au")) {
        return `${minimum} ${unitFormat}x`;
      } else {
        return `${minimum} ${unitFormat}s`;
      }
    }
  
    return `${minimum}${unitFormat}`;
  }

  if (!open || loading) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={() => onClose(false)}>
      <div className="relative bg-white rounded-lg w-[80%] lg:w-[55%] max-w-2xl" onClick={(e) => e.stopPropagation()}>
        <button
          className="absolute -top-3 -right-3 bg-[#639c6e] text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-[#93c57b] shadow-lg"
          onClick={() => closeModal()}
        >
          &#10005;
        </button>

        <div className="flex flex-col md:flex-row">
          <div className="h-44 md:h-auto md:w-1/3 md:h-1/1 overflow-hidden rounded-t-lg md:rounded-r-none md:rounded-l-lg">
            <img
              src={product?.image}
              alt={product?.productName}
              className="w-full h-full object-cover overflow-hidden"
            />
          </div>

          <div className="md:w-2/3 p-6 flex flex-col justify-between bg-[#f6f6f6] rounded-lg">
            <div>
              <div className="bg-[#93c57b] text-white text-sm rounded px-4 py-1 inline-block mb-4">
                {product?.category || "Autre"}
              </div>
              {product?.labels?.map((label, index) => (
                <div key={index} className="bg-[#93c57b] text-white text-sm rounded px-4 py-1 inline-block mb-4 ml-3">
                  {label}
                </div>
              ))}
              <h2 className="text-xl font-bold mb-4">{product?.productName}</h2>
              <p className="text-gray-700 mb-4 overflow-auto max-h-24" dangerouslySetInnerHTML={{ __html: product?.description || "Pas de description" }} />
              <hr className="mb-4" />
              <div className="text-sm text-gray-500 space-y-1">
                <p>Colis : {formatUnit(product?.minimum, product?.unitFormat)}</p>
                <p>TVA : {product?.taxRate}%</p>
                <p className={`mt-6 ${product?.isAvailable ? "text-gray-500" : "text-red-500"}`}>
                  {product ? (
                    product.stock > 0 ? <span className='text-primary'>En stock</span> : <span className='text-accent'>Rupture de stock</span>
                  ) : (
                    <span className='text-gray-500'>Produit non disponible</span>
                  )}
                </p>
              </div>
            </div>

            <div className="mt-6">
              <div className="flex mb-4 items-end justify-between">
                <span className="text-2xl font-bold">{((product?.price || 0) * (product?.minimum || 0)).toLocaleString("fr-FR", { minimumFractionDigits: 2 })}€</span>
                <span className="text-md font-bold text-gray-400 ml-3 mt-1">{product?.price.toLocaleString("fr-FR", { minimumFractionDigits: 2 })}€/{product?.unitFormat}</span>
              </div>

              {quantity > 0 ? (
                <div className="flex items-center border-primary border-2 rounded-lg overflow-hidden">
                  <div className='h-9 w-10 flex justify-center items-center bg-white cursor-pointer hover:bg-gray-200'>
                    <Minus
                      onClick={handleRemoveFromCart}
                      size={22}
                      className='text-primary'
                    />
                  </div>  
                  <div className='h-9 flex-1 bg-primary flex justify-center items-center'>
                    <p className='text-white font-bold'>{quantity}</p>
                  </div> 
                  <div className='h-9 w-10 flex justify-center items-center bg-white cursor-pointer hover:bg-gray-200'>
                    <Plus
                      onClick={handleAddToCart}
                      size={22}
                      className='text-primary'
                    />
                  </div>
                </div>
              ) : (
                <Button onClick={handleAddToCart} disabled={product ? product?.stock < 1 : true} className="w-full text-white py-1 rounded-lg">
                  Ajouter au panier
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailModal;
